import { toast } from 'react-toastify';

import { createDefaultHeaders } from '../api';
import { getSession, urlWithAdminQuery } from '../auth';
import type { ServerUnit, Unit } from './types';

// Constants //////////////////////////////////////////////////////////////////

/** API URL */
// const API_URL = process.env.REACT_APP_API_URL

// Utility ////////////////////////////////////////////////////////////////////

/**
 * Process server response from get/list/update unit
 *
 * @param  {object} unNormalizedData - Server response
 * @returns {object} - Passed in object
 */
const handleUnitResponse = (unNormalizedData) => {
  return unNormalizedData;
};

// API Calls //////////////////////////////////////////////////////////////////

/**
 * Create Unit
 *
 * @param {string} userId - User ID
 * @param {string} structureId - Structure ID the unit belongs to
 * @param {object} address - Unit address
 * @param {string} name - Unit name
 * @param {number} pipeDiameter - Pipe diameter
 * @param {string} pipeMaterial - Pipe material
 * @param {number} index - Unit index
 * @returns {Promise} - Created unit or error
 */
export const create = (
  userId: string,
  structureId: string,
  address: Object,
  name: string,
  index: number,
  unitFlag?: 'NON_BILLABLE' | 'COMMON_AREA' | 'SPLIT'[],
): Promise<Unit> => {
  const body = {
    name: name,
    pipe_diameter: '3_4INCH',
    pipe_material: 'COPPER-M',
    structure_id: structureId,
    ix: index,
    address,
    unit_flags: unitFlag ? unitFlag : [],
  };
  return getSession().then((session) =>
    fetch(urlWithAdminQuery('/unit', session.adminEmail), {
      method: 'POST',
      body: JSON.stringify(body),
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) {
          let errorMessage = 'Unknown error';
          try {
            errorMessage = await resp.text();
          } catch {
            errorMessage = resp.statusText || 'Unknown error';
          }

          // Check for permission errors
          if (errorMessage.includes('MANAGE_UNITS')) {
            toast.error(
              "You don't have permission to create units. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else if (errorMessage.includes('Missing required permissions')) {
            toast.error(
              "You don't have permission to perform this action. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else {
            toast.error(`Failed to create unit: ${errorMessage}`, { closeButton: true });
          }
          return Promise.reject(new Error(errorMessage));
        }
        return resp.json();
      })
      .then(handleUnitResponse)
      .catch((err) => {
        console.error(err);
        // Only show generic error if we haven't already shown a specific one
        if (
          !err.message ||
          (!err.message.includes('MANAGE_UNITS') &&
            !err.message.includes('Missing required permissions'))
        ) {
          toast.error(`Error creating unit: ${err.message || 'Unknown error'}`, {
            closeButton: true,
          });
        }
        return Promise.reject(err);
      }),
  );
};

/**
 * Update Unit in backend
 *
 * @param  {object} unit - Unit data to update
 * @returns {Promise} - Result of call to get unit
 */
export const edit = (unit: ServerUnit): Promise<Unit> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/unit/${unit.id}`, session.adminEmail), {
      method: 'PUT',
      body: JSON.stringify(unit),
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) {
          let errorMessage = 'Unknown error';
          try {
            errorMessage = await resp.text();
          } catch {
            errorMessage = resp.statusText || 'Unknown error';
          }

          // Check for permission errors
          if (errorMessage.includes('MANAGE_UNITS')) {
            toast.error(
              "You don't have permission to edit units. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else if (errorMessage.includes('Missing required permissions')) {
            toast.error(
              "You don't have permission to perform this action. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else {
            toast.error(`Failed to edit unit: ${errorMessage}`, { closeButton: true });
          }
          return Promise.reject(new Error(errorMessage));
        }
        return resp.json();
      })
      .then(handleUnitResponse)
      .catch((err) => {
        console.error(err);
        // Only show generic error if we haven't already shown a specific one
        if (
          !err.message ||
          (!err.message.includes('MANAGE_UNITS') &&
            !err.message.includes('Missing required permissions'))
        ) {
          toast.error(`Error updating unit ${unit.id}: ${err.message || 'Unknown error'}`, {
            closeButton: true,
          });
        }
        return Promise.reject(err);
      }),
  );
};

/**
 * Update the PMS config for a unit.
 * Sends a minimal PUT body — the backend preserves all other fields.
 */
export const updateUnitPmsConfig = (
  unitId: string,
  pmsType: string | null,
  pmsConfig: Record<string, unknown> | null,
): Promise<void> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/unit/${unitId}`, session.adminEmail), {
      method: 'PUT',
      body: JSON.stringify({ pms_type: pmsType, pms_config: pmsConfig }),
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    }).then(async (resp) => {
      if (!resp.ok) {
        const errorMessage = await resp.text().catch(() => 'Unknown error');
        throw new Error(errorMessage);
      }
    }),
  );
};

/**
 * Delete unit
 *
 * @param  {string} unitId - Unit ID to delete
 * @returns {Promise} - Result of call to delete unit
 */
export const remove = (unitId: string): Promise<Response> => {
  return getSession().then((session) =>
    fetch(urlWithAdminQuery(`/unit/${unitId}`, session.adminEmail), {
      method: 'DELETE',
      headers: createDefaultHeaders({
        authToken: session.idToken.toString(),
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) {
          let errorMessage = 'Unknown error';
          try {
            errorMessage = await resp.text();
          } catch {
            errorMessage = resp.statusText || 'Unknown error';
          }

          // Check for permission errors
          if (errorMessage.includes('MANAGE_UNITS')) {
            toast.error(
              "You don't have permission to delete units. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else if (errorMessage.includes('Missing required permissions')) {
            toast.error(
              "You don't have permission to perform this action. Please contact your account owner.",
              { closeButton: true, autoClose: 5000 },
            );
          } else {
            toast.error(`Failed to delete unit: ${errorMessage}`, { closeButton: true });
          }
          return Promise.reject(new Error(errorMessage));
        }
        return resp;
      })
      .catch((err) => {
        console.error(err);
        // Only show generic error if we haven't already shown a specific one
        if (
          !err.message ||
          (!err.message.includes('MANAGE_UNITS') &&
            !err.message.includes('Missing required permissions'))
        ) {
          toast.error(`Error deleting unit ${unitId}: ${err.message || 'Unknown error'}`, {
            closeButton: true,
          });
        }
        return Promise.reject(err);
      }),
  );
};
